import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { PLATFORM_META, STATUS_META, imageSrc } from "../lib/api";
import { Check, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

/* The queue as a three-pane console (PRD 23): queue, post preview, gate and
   actions. Three panes from 1280px; two from 768px with the gate under the
   preview; one on a phone, approval-first, a post per screen, tap only.

   Keyboard: J / K move through the queue, A approves, E edits, R rejects. */

const LIMITS = { linkedin: 3000, instagram: 2200, facebook: 63206, threads: 500 };

const Pill = ({ status, held }) => {
  const s = STATUS_META[held ? "pending_approval" : status] || STATUS_META.draft;
  return <span className={`inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${s.cls}`}>
    {held ? "Held" : s.label}</span>;
};

const held = (p) => (p.hold_reasons || []).length > 0;
const source = (p) => (p.source?.feed_name || p.source?.site || p.origin || "Composer").toString();

const Queue = ({ posts, current, onPick }) => (
  <ol className="space-y-2" aria-label="Queue">
    {posts.map((p) => (
      <li key={p.id}>
        <button onClick={() => onPick(p.id)} aria-current={current === p.id}
          className={`w-full text-left rounded-[10px] border p-3 transition-colors duration-fast ${
            current === p.id ? "border-[#6366F1] bg-ac-raised" : "border-ac-border bg-ac-surface hover:bg-ac-raised"}`}>
          <p className="mono-label !text-[10px] text-ac-muted truncate">{source(p)}</p>
          <p className="mt-1 text-[13px] text-ac-text leading-snug line-clamp-2">{p.topic}</p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <Pill status={p.status} held={held(p)} />
            {p.scheduled_at && <span className="font-mono text-[10px] text-ac-muted">{new Date(p.scheduled_at).toLocaleDateString()}</span>}
          </div>
          {held(p) && <p className="mt-1.5 text-[11px] text-[#F9A8D4] line-clamp-2">{p.hold_reasons[0]}</p>}
        </button>
      </li>
    ))}
  </ol>
);

const Preview = ({ post }) => {
  const platforms = post.platforms?.length ? post.platforms : ["linkedin"];
  const [tab, setTab] = useState(platforms[0]);
  useEffect(() => { setTab(platforms[0]); }, [post.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const text = `${post.caption || ""}${post.hashtags?.length ? "\n\n" + post.hashtags.map((h) => `#${h}`).join(" ") : ""}`;
  const limit = LIMITS[tab];
  const img = imageSrc(post);
  const slides = (post.slides || []).filter((s) => s.image_url || s.image_base64);
  return (
    <div className="space-y-3">
      <div className="rounded-[10px] border border-ac-border bg-ac-bg overflow-hidden">
        {img ? <img src={img} alt={post.topic || ""} className="w-full max-h-[420px] object-contain bg-black" />
          : <div className="aspect-[4/5] max-h-[420px] flex items-center justify-center text-[13px] text-ac-muted">
              {post.image_status === "pending" || post.image_status === "generating" ? "The image is rendering…" : "No image"}</div>}
        {slides.length > 1 && (
          <div className="flex gap-1.5 p-2 overflow-x-auto border-t border-ac-border">
            {slides.map((s, i) => <img key={i} src={imageSrc(s)} alt={`Slide ${i + 1}`} className="h-14 w-11 object-cover rounded" />)}
          </div>
        )}
        <div className="p-3 border-t border-ac-border">
          <p className="text-[13px] text-ac-text whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto">{text}</p>
          {limit && (
            <p className={`mt-2 font-mono text-[11px] ${text.length > limit ? "text-[#FCA5A5]" : "text-ac-muted"}`}>
              {text.length} / {limit} characters on {PLATFORM_META[tab]?.label || tab}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Preview per platform">
        {platforms.map((pl) => (
          <button key={pl} role="tab" aria-selected={tab === pl} onClick={() => setTab(pl)}
            className={`rounded-md border px-2.5 py-1 text-[12px] ${tab === pl
              ? "border-[#6366F1] bg-[#191C45] text-[#C7D2FE]" : "border-ac-border text-ac-muted hover:text-ac-text"}`}>
            {PLATFORM_META[pl]?.label || pl}
          </button>
        ))}
      </div>
    </div>
  );
};

const Gate = ({ gate, post, busy, onApprove, onEdit, onReject }) => {
  const review = gate?.review || post.quality?.review;
  const threshold = post.quality?.threshold;
  const reasons = post.hold_reasons?.length ? post.hold_reasons : gate?.problems || [];
  return (
    <div className="space-y-4">
      {review ? (
        <div>
          <p className="font-mono text-[32px] leading-none text-ac-text">
            {Number(review.score).toFixed(1)}<span className="text-[13px] text-ac-muted"> / 10{threshold ? ` · bar ${Number(threshold).toFixed(1)}` : ""}</span>
          </p>
          {review.subscores && Object.keys(review.subscores).length > 0 && (
            <dl className="mt-2 grid grid-cols-4 gap-2">
              {Object.entries(review.subscores).map(([k, v]) => (
                <div key={k}><dt className="mono-label !text-[10px] text-ac-muted">{k}</dt><dd className="font-mono text-[14px] text-ac-text">{v}</dd></div>
              ))}
            </dl>
          )}
          {review.verdict && <p className="mt-2 text-[12px] text-ac-muted">{review.verdict}</p>}
        </div>
      ) : (
        <p className="mono-label text-ac-muted">Hard checks · manual mode</p>
      )}

      {reasons.length > 0 && (
        <div className="rounded-md border border-[#6B1D47] bg-[#3B1029] p-3 space-y-1">
          {reasons.map((r) => <p key={r} className="text-[13px] text-[#F9A8D4]">Held: {r}</p>)}
        </div>
      )}

      {!gate ? (
        <p className="flex items-center gap-2 text-[13px] text-ac-muted"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running the checks…</p>
      ) : (
        <ul className="space-y-1.5" aria-label="Hard checks">
          {gate.checks.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-[13px]">
              <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                c.passed ? "bg-[#4ADE80]/15 text-[#4ADE80]" : "bg-[#F472B6]/15 text-[#F472B6]"}`}>
                {c.passed ? <Check className="w-2.5 h-2.5" strokeWidth={3} /> : <X className="w-2.5 h-2.5" strokeWidth={3} />}
              </span>
              <span className={c.passed ? "text-ac-text" : "text-[#F9A8D4]"}>{c.label}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-3 gap-2 pt-1">
        <button onClick={onApprove} disabled={busy} data-testid="console-approve"
          className="h-10 rounded-md bg-ac-action hover:bg-ac-action-hover text-white text-[14px] font-medium disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Approve
        </button>
        <button onClick={onEdit} disabled={busy}
          className="h-10 rounded-md border border-ac-border text-ac-text hover:bg-ac-raised text-[14px]">Edit</button>
        <button onClick={onReject} disabled={busy}
          className="h-10 rounded-md border border-[#6B2121] text-[#FCA5A5] hover:bg-[#3A1414] text-[14px]">Reject</button>
      </div>
      <p className="hidden md:block font-mono text-[11px] text-ac-muted">J / K move · A approve · E edit · R reject</p>
    </div>
  );
};

export const ApprovalConsole = ({ posts, onDone }) => {
  const queue = useMemo(() => [...posts].sort((a, b) =>
    (held(b) - held(a)) || (a.submitted_at || a.created_at || "").localeCompare(b.submitted_at || b.created_at || "")), [posts]);
  const [currentId, setCurrentId] = useState(queue[0]?.id);
  const [gates, setGates] = useState({});
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!queue.find((p) => p.id === currentId)) setCurrentId(queue[0]?.id);
  }, [queue, currentId]);
  const index = Math.max(0, queue.findIndex((p) => p.id === currentId));
  const post = queue[index];

  // The checks are run again when a post is opened: cheap, model-free, and
  // current after an edit or once an image has finished.
  useEffect(() => {
    if (!post || gates[post.id]) return;
    api.post(`/posts/${post.id}/gate`).then(({ data }) => setGates((g) => ({ ...g, [post.id]: data })))
      .catch(() => setGates((g) => ({ ...g, [post.id]: post.gate || { checks: [], problems: [] } })));
  }, [post, gates]);

  const move = useCallback((d) => {
    if (!queue.length) return;
    setCurrentId(queue[Math.min(queue.length - 1, Math.max(0, index + d))].id);
  }, [queue, index]);

  const approve = useCallback(async () => {
    if (!post || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/approve`);
      if (data.status === "scheduled") toast.success("Approved - it publishes at its scheduled time");
      else if (data.status === "failed") toast.error("Approved, but publishing failed - see Failed");
      else toast.success("Approved and published");
      move(1);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); } finally { setBusy(false); }
  }, [post, busy, move, onDone]);

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/reject`, { reason: reason || "Not approved" });
      toast("Rejected");
      setRejecting(false); setReason("");
      move(1);
      onDone();
    } catch { toast.error("Could not reject"); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/posts/${post.id}`, { caption: editing });
      const { data } = await api.post(`/posts/${post.id}/gate`);
      setGates((g) => ({ ...g, [post.id]: data }));
      setEditing(null);
      toast.success(data.passed ? "Saved - every hard check passes" : "Saved - some checks still fail");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (rejecting || editing !== null || e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "j") { e.preventDefault(); move(1); }
      else if (k === "k") { e.preventDefault(); move(-1); }
      else if (k === "a") { e.preventDefault(); approve(); }
      else if (k === "e" && post) { e.preventDefault(); setEditing(post.caption || ""); }
      else if (k === "r" && post) { e.preventDefault(); setRejecting(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, approve, post, rejecting, editing]);

  if (!post) return null;
  const gate = gates[post.id];

  return (
    <div data-testid="approval-console">
      {/* Phone: one post per screen, approval first. */}
      <div className="md:hidden flex items-center justify-between mb-3">
        <button onClick={() => move(-1)} disabled={index === 0} aria-label="Previous post"
          className="h-10 w-10 rounded-md border border-ac-border flex items-center justify-center disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
        <span className="font-mono text-[12px] text-ac-muted">{index + 1} of {queue.length}</span>
        <button onClick={() => move(1)} disabled={index === queue.length - 1} aria-label="Next post"
          className="h-10 w-10 rounded-md border border-ac-border flex items-center justify-center disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 gap-4 pb-20 md:pb-0 md:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)_340px]">
        <section className="hidden md:block rounded-[10px] border border-ac-border bg-ac-surface p-3 md:row-span-2 xl:row-span-1 max-h-[calc(100vh-14rem)] overflow-y-auto">
          <p className="mono-label text-ac-muted mb-3">1 · Queue <span className="text-ac-text">{queue.length}</span></p>
          <Queue posts={queue} current={post.id} onPick={setCurrentId} />
        </section>
        <section className="min-w-0 rounded-[10px] border border-ac-border bg-ac-surface p-3 sm:p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="mono-label text-ac-muted">2 · Post preview</p>
              <p className="mt-1 text-[15px] text-ac-text font-medium truncate">{post.topic}</p>
            </div>
            <Pill status={post.status} held={held(post)} />
          </div>
          <Preview post={post} />
        </section>
        <section className="min-w-0 rounded-[10px] border border-ac-border bg-ac-surface p-3 sm:p-4">
          <p className="mono-label text-ac-muted mb-3">3 · Gate &amp; actions</p>
          <Gate gate={gate} post={post} busy={busy} onApprove={approve}
            onEdit={() => setEditing(post.caption || "")} onReject={() => setRejecting(true)} />
        </section>
      </div>

      {/* Phone: the decision stays in reach. Buttons, no swipe - an accidental
          swipe must never approve a post. */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-ac-border bg-ac-bg px-4 py-3 grid grid-cols-3 gap-2">
        <button onClick={approve} disabled={busy} className="h-11 rounded-md bg-ac-action text-white text-[15px] font-medium disabled:opacity-60">Approve</button>
        <button onClick={() => setEditing(post.caption || "")} disabled={busy} className="h-11 rounded-md border border-ac-border text-ac-text text-[15px]">Edit</button>
        <button onClick={() => setRejecting(true)} disabled={busy} className="h-11 rounded-md border border-[#6B2121] text-[#FCA5A5] text-[15px]">Reject</button>
      </div>

      <Dialog open={rejecting} onOpenChange={(o) => !o && setRejecting(false)}>
        <DialogContent className="bg-ac-surface border-ac-border text-ac-text">
          <DialogHeader><DialogTitle className="font-display">Reject post</DialogTitle></DialogHeader>
          <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Why? The reason is kept so the same post is not proposed again."
            className="w-full bg-ac-bg border border-ac-border rounded-md px-3 py-2 text-[14px] text-ac-text focus:outline-none focus:border-ac-link" />
          <DialogFooter>
            <button onClick={() => setRejecting(false)} className="h-10 px-4 rounded-md text-[14px] text-ac-muted hover:text-ac-text">Cancel</button>
            <button onClick={reject} disabled={busy} className="h-10 px-4 rounded-md border border-[#6B2121] bg-[#3A1414] text-[#FCA5A5] text-[14px]">Reject post</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-ac-surface border-ac-border text-ac-text max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Edit caption</DialogTitle></DialogHeader>
          <textarea autoFocus value={editing || ""} onChange={(e) => setEditing(e.target.value)} rows={10}
            className="w-full bg-ac-bg border border-ac-border rounded-md px-3 py-2 text-[14px] text-ac-text leading-relaxed focus:outline-none focus:border-ac-link" />
          <p className="font-mono text-[11px] text-ac-muted">{(editing || "").length} characters · the checks run again when you save</p>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-md text-[14px] text-ac-muted hover:text-ac-text">Cancel</button>
            <button onClick={saveEdit} disabled={busy} className="h-10 px-4 rounded-md bg-ac-action hover:bg-ac-action-hover text-white text-[14px] font-medium">Save and re-check</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalConsole;
